/* ============================================================
   ATHLO+ — Effects & interaction enhancements
   Loads AFTER main.js. Everything is guarded so the same file
   runs on the homepage and the Gear / Tribe pages.
   ============================================================ */
(function () {
  'use strict';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGSAP = typeof gsap !== 'undefined';
  const fine = window.matchMedia('(pointer: fine)').matches;
  if (hasGSAP && typeof ScrollTrigger !== 'undefined') gsap.registerPlugin(ScrollTrigger);

  /* --------------------------------------------------------
     PAGE TRANSITIONS (multi-page)
  -------------------------------------------------------- */
  function initPageTrans() {
    const trans = document.querySelector('.page-trans');
    if (!trans) return;
    const panels = trans.querySelectorAll('.page-trans__panel');
    const logo = document.querySelector('.page-trans__logo');

    // Reveal on entry. ADDON 2026-10-08: the reveal now RUNS IN CSS from first paint (the-hub.css, "CSS entry
    // reveal") instead of waiting for this deferred file - on a throttled phone that was ~4 s of a covered page.
    // This block only finishes it: once the last panel's ptReveal has ended, hide the overlay and add .pt-js,
    // which switches the CSS animations off so the outbound cover below is not overridden. If the CSS reveal is
    // absent (an older cached stylesheet), the original GSAP reveal runs exactly as before.
    const lastPanel = panels[panels.length - 1];
    const cssReveal = !!lastPanel && getComputedStyle(lastPanel).animationName === 'ptReveal';
    const finishEntry = function () {
      if (trans.classList.contains('pt-js')) return;
      trans.classList.add('pt-js');
      trans.style.display = 'none';
      // Not touching the logo: its CSS fade ends at opacity 0 and .pt-js leaves it at the stylesheet's 0. (And
      // `navigating` is declared further down with let - reading it from here can throw on the first call.)
    };
    if (reduce) {
      finishEntry();
    } else if (cssReveal) {
      const running = lastPanel.getAnimations && lastPanel.getAnimations().some(function (a) {
        return a.animationName === 'ptReveal' && a.playState === 'running';
      });
      if (running) {
        lastPanel.addEventListener('animationend', finishEntry, { once: true });
        setTimeout(finishEntry, 1500);   // a backgrounded tab can hold animationend back
      } else {
        finishEntry();
      }
    } else if (hasGSAP) {
      trans.classList.add('pt-js');
      gsap.set(trans, { pointerEvents: 'none' });
      gsap.set(panels, { scaleY: 1, transformOrigin: 'top' });
      gsap.to(panels, { scaleY: 0, duration: 0.7, ease: 'power3.inOut', stagger: 0.06,
        onComplete: function () { trans.style.display = 'none'; } });
      if (logo) gsap.fromTo(logo, { opacity: 1 }, { opacity: 0, duration: 0.4 });
    } else {
      finishEntry();
    }

    /* === ADDON: outbound page-transition matcher (2026-08-02) ==============
       WHAT WAS WRONG. The cover wipe only ever fired for `href` ending in
       `.html` or exactly `/`. That test is a leftover from the headless static
       build, where pages really were `gear.html` and `tribes.html`. On Shopify
       every internal link is `/pages/…`, `/products/…`, `/collections/…`, so
       there are ZERO `.html` hrefs in the theme and this whole branch had never
       run in production except on the brand logo. The entry wipe worked; the
       outbound half was dead code.

       WHY IT IS CAREFUL. This is a document-level click handler on a live site:
       it sees every link click on every page, and a false positive means a link
       that silently does not work. So the matcher is a deny-by-default list and
       anything it cannot positively identify as a plain, same-origin, left-click
       navigation is handed straight back to the browser.

       Revert: restore the old two-line `isPage` test, or drop the whole handler
       (the entry wipe in the block above is independent of it).
       Escape hatch: put `data-no-trans` on any link that must never wipe. ----- */
    let navigating = false;

    /* Returns the absolute URL to navigate to, or null to let the browser handle
       the click itself. Kept as a named function so _verify/pagetrans-test.mjs can
       slice it out of THIS file and test the deployed logic rather than a copy. */
    function outboundTarget(a, e) {
      if (!a || !e) return null;
      // Somebody already claimed this click: the in-page smooth-scroll handler in
      // hub-app.js, or the cart-drawer / predictive-search toggles in hub-shop.js,
      // which are anchors with real hrefs that JS turns into panels.
      if (e.defaultPrevented) return null;
      if (e.button !== 0) return null;                                   // middle/right click
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null; // new tab / window / download
      if (a.hasAttribute('download') || a.hasAttribute('data-no-trans')) return null;
      if (a.hasAttribute('data-cart-toggle') || a.hasAttribute('data-search-toggle')) return null;
      if (a.target && a.target !== '_self') return null;
      const raw = a.getAttribute('href');
      if (!raw || raw.charAt(0) === '#') return null;                    // in-page anchor
      if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && !/^https?:/i.test(raw)) return null; // mailto:, tel:, javascript:
      let url;
      try { url = new URL(a.href, location.href); } catch (_) { return null; }
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
      if (url.origin !== location.origin) return null;                   // external site
      // Same page — a hash-only or identical link is not a navigation to cover.
      if (url.pathname === location.pathname && url.search === location.search) return null;
      return url.href;
    }

    // Cover on internal page navigation
    document.addEventListener('click', function (e) {
      if (navigating) { e.preventDefault(); return; }   // a wipe is already running
      const a = e.target.closest('a');
      if (!a) return;
      if (reduce || !hasGSAP) return;                   // let it navigate normally
      const href = outboundTarget(a, e);
      if (!href) return;

      e.preventDefault();
      navigating = true;
      const leave = function () { window.location.href = href; };
      trans.style.display = '';
      gsap.set(trans, { pointerEvents: 'all' });
      gsap.set(panels, { transformOrigin: 'bottom' });
      const tl = gsap.timeline({ onComplete: leave });
      if (logo) tl.fromTo(logo, { opacity: 0, scale: 0.94 }, { opacity: 1, scale: 1, duration: 0.35, ease: 'power3.out' }, 0.18);
      tl.fromTo(panels, { scaleY: 0 }, { scaleY: 1, duration: 0.55, ease: 'power3.inOut', stagger: 0.05 }, 0);
      // Failsafe — the same lesson as the loader's. A backgrounded tab pauses
      // requestAnimationFrame and therefore GSAP, so onComplete may never fire and
      // the visitor would be stranded on a covered page that never goes anywhere.
      // The timeline runs ~0.75s; navigate regardless at 1.6s.
      setTimeout(leave, 1600);
      // …and if we are STILL here well after that, the navigation never happened
      // (a load the browser blocked or cancelled). Give the page back. See
      // endCoverWipe() for why leaving `navigating` true is not a cosmetic problem.
      setTimeout(function () { if (navigating) endCoverWipe(); }, 3000);
    });
    /* === END ADDON: outbound page-transition matcher === */

    // Failsafe: the logo lives OUTSIDE .page-trans (z-9501), so hiding the panels
    // never hides it — if its entry fade-out is interrupted (backgrounded tab pauses
    // rAF, tween killed), it ghosts a translucent ATHLO+ over content forever.
    // Force it out once the entry wipe is done; re-check when the tab foregrounds.
    //
    // THE GUARD USED TO BE A LATCH THAT NOTHING EVER RELEASED (fixed 2026-08-15).
    // It read `trans.style.pointerEvents === 'all'` — a flag the cover wipe sets and
    // no code path anywhere clears — so from the athlete's FIRST internal link click
    // onward, clearLogo() was a no-op for the rest of that document's life. That is
    // invisible right up until the document comes back, because the wipe leaves the
    // logo at opacity 1 on its way out:
    //
    //   tap a link → wipe covers → logo opacity 1, pointerEvents 'all' → navigate
    //   → press Back → bfcache restores the frozen heap → pageshow hides the panels,
    //     clearLogo() bails on the stale latch → a full-screen ATHLO+ wordmark,
    //     position:fixed, sitting over the page and staying put as you scroll.
    //
    // Reported as "rare cases where the ATHLO+ logo is on screen when they scroll
    // down". Rare because it needs a bfcache restore — which is the common case on
    // iOS Safari's back-swipe, and athlo.sg sends no `no-store`, so the page is
    // eligible (checked 2026-08-15).
    //
    // The same stale latch had a second, worse effect. `navigating` is restored true
    // as well, and the click handler's first line is
    // `if (navigating) { e.preventDefault(); return; }` — which runs BEFORE the
    // `closest('a')` test, so it swallows every click anywhere on the page, not just
    // links. A restored page was visually ghosted AND completely dead to input.
    //
    // So the guard is now `navigating`, which means what the old one only looked like
    // it meant: a cover wipe is running RIGHT NOW. Everything that can outlive a wipe
    // releases it through endCoverWipe().
    function clearLogo() {
      if (!logo) return;
      if (navigating) return;   // a wipe is in flight; it owns the logo until it lands
      if (hasGSAP) gsap.killTweensOf(logo);
      logo.style.opacity = 0;
    }

    // Release the cover wipe and everything it holds hostage: the logo, the panels,
    // and every click on the document. Nothing else may leave `navigating` true.
    function endCoverWipe() {
      navigating = false;
      if (hasGSAP) gsap.set(trans, { pointerEvents: 'none' });
      else trans.style.pointerEvents = 'none';
      trans.style.display = 'none';
      clearLogo();
    }

    setTimeout(clearLogo, 1600);
    setTimeout(clearLogo, 4000);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) clearLogo(); });

    // Restore overlay if user navigates back via bfcache. A restored document is NOT
    // mid-navigation, whatever its frozen heap says — so release the wipe outright
    // rather than only hiding the panels it drew.
    window.addEventListener('pageshow', function (ev) {
      if (ev.persisted) endCoverWipe();
    });
  }

  /* --------------------------------------------------------
     TEXT SCRAMBLE  (data-scramble)
  -------------------------------------------------------- */
  function scramble(el, finalText) {
    const chars = '▖▘▝▗◢◣◤◥/\\|—=+*#';
    let frame = 0;
    const total = 28;
    const start = finalText.split('');
    function step() {
      let out = '';
      for (let i = 0; i < start.length; i++) {
        if (i < (frame / total) * start.length) out += finalText[i];
        else if (finalText[i] === ' ') out += ' ';
        else out += chars[Math.floor(Math.random() * chars.length)];
      }
      el.textContent = out;
      frame++;
      if (frame <= total) requestAnimationFrame(step);
      else el.textContent = finalText;
    }
    step();
  }
  function initScramble() {
    if (reduce) return;
    document.querySelectorAll('[data-scramble]').forEach(function (el) {
      const txt = el.textContent;
      if (typeof ScrollTrigger === 'undefined') return;
      ScrollTrigger.create({ trigger: el, start: 'top 90%', once: true,
        onEnter: function () { scramble(el, txt); } });
    });
  }

  /* --------------------------------------------------------
     CHARACTER REVEAL  (data-chars)
  -------------------------------------------------------- */
  function initChars() {
    if (!hasGSAP || typeof ScrollTrigger === 'undefined') return;
    document.querySelectorAll('[data-chars]').forEach(function (el) {
      const text = el.textContent;
      el.innerHTML = '';
      const frag = document.createDocumentFragment();
      text.split('').forEach(function (c) {
        const s = document.createElement('span');
        s.className = 'char';
        s.textContent = c === ' ' ? ' ' : c;
        frag.appendChild(s);
      });
      el.appendChild(frag);
      if (reduce) return;
      gsap.set(el.querySelectorAll('.char'), { yPercent: 120, opacity: 0 });
      ScrollTrigger.create({ trigger: el, start: 'top 88%', once: true,
        onEnter: function () {
          gsap.to(el.querySelectorAll('.char'), { yPercent: 0, opacity: 1, duration: 0.7, ease: 'power3.out', stagger: 0.022 });
        } });
    });
  }

  /* --------------------------------------------------------
     FANNED / DRAGGABLE FEED COLLAGE
  -------------------------------------------------------- */
  function initFeed() {
    const stage = document.querySelector('.feed__stage');
    if (!stage) return;
    const cards = Array.from(stage.querySelectorAll('.feed-card'));
    const n = cards.length;
    const mid = (n - 1) / 2;
    const spread = Math.min(9, 60 / n);
    const gapX = stage.clientWidth < 600 ? 42 : 78;
    const lift = 16;

    const base = cards.map(function (card, i) {
      return { x: (i - mid) * gapX, y: Math.abs(i - mid) * lift - lift, rot: (i - mid) * spread, z: 100 - Math.abs(i - mid) };
    });

    function place(i, anim) {
      const b = base[i];
      const props = { x: b.x, y: b.y, rotation: b.rot, zIndex: b.z, scale: 1 };
      cards[i].style.zIndex = b.z;
      if (hasGSAP) (anim ? gsap.to : gsap.set)(cards[i], anim ? Object.assign({ duration: 0.6, ease: 'power3.out', overwrite: 'auto' }, props) : props);
    }

    // intro
    if (hasGSAP && !reduce && typeof ScrollTrigger !== 'undefined') {
      cards.forEach(function (c) { gsap.set(c, { x: 0, y: 80, rotation: 0, opacity: 0 }); });
      ScrollTrigger.create({ trigger: stage, start: 'top 80%', once: true,
        onEnter: function () {
          cards.forEach(function (c, i) {
            gsap.to(c, { opacity: 1, x: base[i].x, y: base[i].y, rotation: base[i].rot, duration: 0.8, ease: 'power3.out', delay: i * 0.05 });
          });
        } });
    } else {
      cards.forEach(function (c, i) { place(i, false); c.style.opacity = 1; });
    }

    // hover parallax spread (desktop) — rAF-throttled, skips the card you're
    // actually hovering or dragging so it can't fight the per-card lift below.
    if (fine && hasGSAP) {
      let pmx = 0, pmy = 0, queued = false;
      function applyParallax() {
        queued = false;
        cards.forEach(function (c, i) {
          if (c.__dragging || c.__hover) return;
          const depth = (i - mid);
          gsap.to(c, { x: base[i].x + pmx * depth * 14, y: base[i].y + pmy * 10, duration: 0.5, ease: 'power2.out', overwrite: 'auto' });
        });
      }
      stage.addEventListener('mousemove', function (e) {
        const r = stage.getBoundingClientRect();
        pmx = (e.clientX - r.left) / r.width - 0.5;
        pmy = (e.clientY - r.top) / r.height - 0.5;
        if (!queued) { queued = true; requestAnimationFrame(applyParallax); }
      });
      stage.addEventListener('mouseleave', function () {
        cards.forEach(function (c, i) { c.__hover = false; if (!c.__dragging) place(i, true); });
      });

      // per-card hover: lift the hovered card, bring it forward, ease the others off it
      cards.forEach(function (card, i) {
        card.addEventListener('mouseenter', function () {
          if (card.__dragging) return;
          card.__hover = true; card.style.zIndex = 300;
          gsap.to(card, { x: base[i].x, y: base[i].y - 26, rotation: base[i].rot * 0.35, scale: 1.06, duration: 0.4, ease: 'power3.out', overwrite: 'auto' });
        });
        card.addEventListener('mouseleave', function () {
          if (card.__dragging) return;
          card.__hover = false;
          place(i, true);
        });
      });
    }

    // drag
    cards.forEach(function (card, i) {
      let sx = 0, sy = 0, ox = 0, oy = 0, dragging = false;
      card.addEventListener('pointerdown', function (e) {
        dragging = true; card.__dragging = true; card.__hover = false; card.setPointerCapture(e.pointerId);
        sx = e.clientX; sy = e.clientY;
        ox = gsap.getProperty(card, 'x'); oy = gsap.getProperty(card, 'y');
        card.style.zIndex = 200;
        if (hasGSAP) gsap.killTweensOf(card);
      });
      card.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        const nx = ox + (e.clientX - sx);
        const ny = oy + (e.clientY - sy);
        if (hasGSAP) gsap.set(card, { x: nx, y: ny });
      });
      function end() {
        if (!dragging) return;
        dragging = false; card.__dragging = false;
        place(i, true);
      }
      card.addEventListener('pointerup', end);
      card.addEventListener('pointercancel', end);
    });
  }

  /* --------------------------------------------------------
     MAGIC BENTO — cursor spotlight + glowing borders (data-bento).
     The grid gets a light that follows the pointer; each card gets
     a lime/blue border-glow that tracks the cursor. Desktop only.
  -------------------------------------------------------- */
  function initBento() {
    if (!fine) return;
    const grids = document.querySelectorAll('[data-bento]');
    if (!grids.length) return;

    function spawnParticles(card) {
      if (reduce || card.querySelector('.bento-particles')) return;
      const box = document.createElement('span');
      box.className = 'bento-particles'; box.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 7; i++) {
        const p = document.createElement('i');
        p.style.left = (8 + Math.random() * 84) + '%';
        p.style.top = (15 + Math.random() * 75) + '%';
        p.style.animationDelay = (Math.random() * 2.2).toFixed(2) + 's';
        p.style.animationDuration = (3 + Math.random() * 2).toFixed(2) + 's';
        box.appendChild(p);
      }
      card.appendChild(box);
    }
    function clearParticles(card) { const b = card.querySelector('.bento-particles'); if (b) b.remove(); }

    grids.forEach(function (grid) {
      const cards = Array.prototype.slice.call(grid.querySelectorAll('[data-bento-card]'));
      grid.addEventListener('pointermove', function (e) {
        const gr = grid.getBoundingClientRect();
        grid.style.setProperty('--bx', (e.clientX - gr.left) + 'px');
        grid.style.setProperty('--by', (e.clientY - gr.top) + 'px');
        grid.classList.add('is-lit');
      }, { passive: true });
      grid.addEventListener('pointerleave', function () { grid.classList.remove('is-lit'); });

      cards.forEach(function (card) {
        card.addEventListener('pointermove', function (e) {
          const r = card.getBoundingClientRect();
          const lx = e.clientX - r.left, ly = e.clientY - r.top;
          card.style.setProperty('--mx', lx + 'px');
          card.style.setProperty('--my', ly + 'px');
          if (!reduce) {
            const px = lx / r.width - 0.5, py = ly / r.height - 0.5;
            card.style.transform = 'perspective(900px) rotateX(' + (-py * 7).toFixed(2) + 'deg) rotateY(' + (px * 9).toFixed(2) + 'deg) translateY(-6px) scale(1.02)';
          }
        }, { passive: true });
        card.addEventListener('pointerenter', function () { spawnParticles(card); });
        card.addEventListener('pointerleave', function () { card.style.transform = ''; clearParticles(card); });
      });
    });
  }

  /* --------------------------------------------------------
     COMMUNITY WALL — rAF marquee (data-wall). Continuous motion,
     pauses only while a card is hovered, idles when off-screen.
     The CSS animation is the no-JS fallback; this takes over and
     owns the transform (.is-js disables the CSS keyframes).
  -------------------------------------------------------- */
  function initWall() {
    const root = document.querySelector('[data-wall]');
    if (!root || reduce) return;
    const tracks = Array.prototype.slice.call(root.querySelectorAll('[data-wall-track]'));
    if (!tracks.length) return;

    const states = tracks.map(function (t) {
      t.classList.add('is-js');
      return {
        el: t,
        base: t.innerHTML,                                          // original 2 sets from Liquid
        dur: parseFloat(getComputedStyle(t).getPropertyValue('--dur')) || 60,
        dir: t.hasAttribute('data-wall-reverse') ? 1 : -1,
        unit: 0, offset: 0, pause: false
      };
    });

    // (re)build a track: reset to one source block, measure one set, then clone
    // whole blocks until it always overflows the row — no gaps for any count.
    function setup(s) {
      s.el.innerHTML = s.base;
      s.unit = s.el.scrollWidth / 2;                                // width of ONE set
      const cw = (s.el.parentElement && s.el.parentElement.clientWidth) || window.innerWidth;
      let guard = 0;
      while (s.el.scrollWidth < cw + s.unit + 60 && guard < 8) { s.el.insertAdjacentHTML('beforeend', s.base); guard++; }
      s.offset = s.dir > 0 ? -s.unit : 0;
      s.el.style.transform = 'translate3d(' + s.offset + 'px,0,0)';
      s.el.querySelectorAll('.cfeed-card').forEach(function (c) {
        c.addEventListener('mouseenter', function () { s.pause = true; });
        c.addEventListener('mouseleave', function () { s.pause = false; });
      });
    }
    states.forEach(setup);

    let rt = null;
    window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(function () { states.forEach(setup); }, 250); });

    let running = false, last = 0;
    function frame(now) {
      if (!running) return;
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0; last = now;
      states.forEach(function (s) {
        if (s.pause || !s.unit) return;
        s.offset += s.dir * (s.unit / s.dur) * dt;
        if (s.offset <= -s.unit) s.offset += s.unit;
        else if (s.offset >= 0) s.offset -= s.unit;
        s.el.style.transform = 'translate3d(' + s.offset.toFixed(2) + 'px,0,0)';
      });
      requestAnimationFrame(frame);
    }
    function start() { if (!running) { running = true; last = 0; requestAnimationFrame(frame); } }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) { if (en[0].isIntersecting) start(); else running = false; }, { threshold: 0 }).observe(root);
    } else { start(); }
  }

  /* --------------------------------------------------------
     HORIZONTAL GEAR STRIP (drag + wheel scroll)
  -------------------------------------------------------- */
  function initHStrip() {
    document.querySelectorAll('[data-hstage]').forEach(function (rail) {
      const scroller = rail.querySelector('[data-hscroll]');
      if (!scroller) return;
      const strip = scroller.querySelector('.hstrip');
      const prev = rail.querySelector('[data-hprev]');
      const next = rail.querySelector('[data-hnext]');
      const progress = rail.querySelector('[data-hprogress]');
      const loop = rail.hasAttribute('data-hloop');   // seamless infinite mode

      function maxScroll() { return Math.max(0, scroller.scrollWidth - scroller.clientWidth); }
      // one "page" = a card width + the strip gap
      function stepSize() {
        const card = strip && strip.children[0];
        if (!card) return scroller.clientWidth * 0.8;
        const gap = strip ? parseFloat(getComputedStyle(strip).columnGap || getComputedStyle(strip).gap || 0) : 0;
        return card.getBoundingClientRect().width + (gap || 0);
      }
      function sync() {
        const max = maxScroll(), x = scroller.scrollLeft;
        if (loop) {                                    // no start/end in loop mode
          if (progress) progress.style.width = '100%';
          if (prev) prev.hidden = false;
          if (next) next.hidden = false;
          return;
        }
        if (progress) progress.style.width = (max <= 1 ? 100 : Math.min(100, x / max * 100)) + '%';
        if (prev) prev.hidden = x <= 4;
        if (next) next.hidden = x >= max - 4 || max <= 1;
      }
      function nudge(dir) { scroller.scrollBy({ left: dir * stepSize(), behavior: reduce ? 'auto' : 'smooth' }); }

      /* ---- seamless infinite loop -----------------------------------------
         Clone the strip's cards into buffers on both sides (enough to cover the
         viewport), start scrolled at the real set, and teleport by one set-width
         whenever the user crosses into a buffer. All sets are identical so the
         jump is invisible → endless scroll in either direction with no hard edge.
         Fails safe: if there's nothing to overflow, loop stays off.            */
      let setWidth = 0;
      function buildLoop() {
        if (scroller.clientWidth <= 1) return false;   // not laid out yet — wait (a reveal/transition can start it at 0)
        const originals = Array.prototype.slice.call(strip.children);
        if (!originals.length) return false;
        const gap = parseFloat(getComputedStyle(strip).columnGap || getComputedStyle(strip).gap || 0) || 0;
        function measureSet(nodes) {
          let w = 0; nodes.forEach(function (n) { w += n.getBoundingClientRect().width + gap; });
          return w;
        }
        setWidth = measureSet(originals);
        if (setWidth <= scroller.clientWidth * 0.25) return false;   // implausibly small = still collapsed; retry later
        // how many clone-sets each side so a buffer always covers the viewport
        const sets = Math.max(1, Math.ceil(scroller.clientWidth / setWidth));
        function cloneSet(where) {
          // build the set as an ordered fragment so a prepended buffer keeps
          // 1·2·3·4 order (inserting card-by-card before firstChild reverses it,
          // which put two identical cards side-by-side at the seam).
          const frag = document.createDocumentFragment();
          originals.forEach(function (n) {
            const c = n.cloneNode(true);
            c.classList.add('is-clone');
            c.setAttribute('aria-hidden', 'true');
            if (c.tagName === 'A') c.setAttribute('tabindex', '-1');
            c.querySelectorAll && c.querySelectorAll('a,button,[tabindex]').forEach(function (f) { f.setAttribute('tabindex', '-1'); });
            frag.appendChild(c);
          });
          if (where === 'before') strip.insertBefore(frag, strip.firstChild);
          else strip.appendChild(frag);
        }
        for (let i = 0; i < sets; i++) { cloneSet('after'); cloneSet('before'); }

        // Land on the real (middle) set. Post-navigation scroll-restoration can
        // clobber this, so re-assert it until the user actually touches the rail:
        // on load, and — decisively — the first time the rail scrolls into view.
        let touched = false;
        function center() { if (touched) return; scroller.style.scrollBehavior = 'auto'; scroller.scrollLeft = setWidth * sets; }
        ['pointerdown', 'wheel', 'keydown', 'touchstart'].forEach(function (ev) {
          scroller.addEventListener(ev, function () { touched = true; }, { passive: true });
        });
        prev && prev.addEventListener('click', function () { touched = true; });
        next && next.addEventListener('click', function () { touched = true; });
        center();
        window.addEventListener('load', center);
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver(function (es) {
            es.forEach(function (e) { if (e.isIntersecting) { center(); io.disconnect(); } });
          }, { threshold: 0.35 });
          io.observe(rail);
        }

        // teleport by one set-width whenever we cross into a buffer → seamless
        let ticking = false;
        scroller.addEventListener('scroll', function () {
          if (ticking) return; ticking = true;
          requestAnimationFrame(function () {
            const lo = setWidth * (sets - 0.5), hi = setWidth * (sets + 0.5);
            const prevBehav = scroller.style.scrollBehavior; scroller.style.scrollBehavior = 'auto';
            if (scroller.scrollLeft < lo) scroller.scrollLeft += setWidth;
            else if (scroller.scrollLeft > hi) scroller.scrollLeft -= setWidth;
            scroller.style.scrollBehavior = prevBehav;
            ticking = false;
          });
        }, { passive: true });
        return true;
      }
      if (prev) prev.addEventListener('click', function () { nudge(-1); });
      if (next) next.addEventListener('click', function () { nudge(1); });

      // drag to scroll
      let down = false, startX = 0, startScroll = 0, moved = false;
      scroller.addEventListener('pointerdown', function (e) {
        down = true; moved = false; startX = e.clientX; startScroll = scroller.scrollLeft;
        scroller.classList.add('is-grabbing');
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        scroller.scrollLeft = startScroll - dx;
      });
      window.addEventListener('pointerup', function () { down = false; scroller.classList.remove('is-grabbing'); });
      // prevent click navigation right after a drag
      scroller.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', function (e) { if (moved) { e.preventDefault(); } });
      });
      // wheel -> horizontal (vertical intent only; let real horizontal trackpad
      // gestures scroll the rail natively). Releases to the page at either end so
      // a downward scroll past the last card keeps scrolling the page instead of
      // trapping it under the cursor.
      scroller.addEventListener('wheel', function (e) {
        if (loop) return;                                     // infinite: never hijack vertical wheel — let the page scroll past
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // horizontal gesture — leave native
        const max = maxScroll();
        if (max <= 1) return;                                 // nothing to scroll — don't hijack
        const atStart = scroller.scrollLeft <= 0, atEnd = scroller.scrollLeft >= max - 1;
        if ((e.deltaY > 0 && atEnd) || (e.deltaY < 0 && atStart)) return; // at edge — release to page
        scroller.scrollLeft += e.deltaY;
        e.preventDefault();
      }, { passive: false });

      scroller.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);

      // Arm the infinite loop LAZILY — only once the rail is on-screen AND laid
      // out (a reveal/transition can start the section at 0 width, which would
      // mis-measure the set width). Snap off so teleports don't fight it.
      if (loop) {
        scroller.style.scrollSnapType = 'none';
        let armed = false;
        function tryArm() { if (!armed) armed = buildLoop(); return armed; }
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver(function (es) {
            es.forEach(function (e) { if (e.isIntersecting && tryArm()) io.disconnect(); });
          }, { threshold: 0.2 });
          io.observe(rail);
        }
        window.addEventListener('load', tryArm);
        let t = 0; const iv = setInterval(function () { if (tryArm() || t++ > 25) clearInterval(iv); }, 200);
      }

      sync();
    });
  }

  /* --------------------------------------------------------
     GEAR PAGE — sticky stage tracker
  -------------------------------------------------------- */
  function initStageTracker() {
    const stages = Array.from(document.querySelectorAll('[data-stage]'));
    const items = Array.from(document.querySelectorAll('[data-stage-link]'));
    const bar = document.querySelector('.stage-track__bar i');
    if (!stages.length || typeof ScrollTrigger === 'undefined') return;

    function setActive(idx) {
      items.forEach(function (it, i) { it.classList.toggle('is-active', i === idx); });
      if (bar) bar.style.transform = 'scaleX(' + ((idx + 1) / stages.length) + ')';
    }
    setActive(0);

    stages.forEach(function (st, i) {
      ScrollTrigger.create({ trigger: st, start: 'top 55%', end: 'bottom 55%',
        onEnter: function () { setActive(i); },
        onEnterBack: function () { setActive(i); } });
    });

    items.forEach(function (it, i) {
      it.addEventListener('click', function () {
        const target = stages[i];
        if (window.__lenis) window.__lenis.scrollTo(target, { offset: -90 });
        else target.scrollIntoView({ behavior: 'smooth' });
      });
    });
  }

  /* --------------------------------------------------------
     CLIP REVEALS  (data-clip)
  -------------------------------------------------------- */
  function initClip() {
    if (!hasGSAP || typeof ScrollTrigger === 'undefined' || reduce) return;
    gsap.utils.toArray('[data-clip]').forEach(function (el) {
      gsap.to(el, { clipPath: 'inset(0 0% 0 0)', duration: 1.1, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
    });
  }

  /* --------------------------------------------------------
     HERO PARALLAX (homepage)
  -------------------------------------------------------- */
  function initHeroParallax() {
    if (!hasGSAP || typeof ScrollTrigger === 'undefined' || reduce) return;
    const hero = document.getElementById('hero');
    const headline = document.querySelector('.hero__headline');
    const glow = document.querySelector('.hero__glow');
    if (!hero || !headline) return;
    gsap.to(headline, { yPercent: -18, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
    gsap.to('.hero__top', { yPercent: -40, opacity: 0, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'center top', scrub: true } });
    if (glow) gsap.to(glow, { scale: 1.5, opacity: 0.4, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });

    // Depth: content drifts subtly against the pointer (desktop only). Applied
    // to x/y (separate from the yPercent scroll tween above, so no conflict).
    if (fine) {
      const inner = document.querySelector('.hero__inner');
      if (inner) {
        const xi = gsap.quickTo(inner, 'x', { duration: 0.9, ease: 'power3.out' });
        const yi = gsap.quickTo(inner, 'y', { duration: 0.9, ease: 'power3.out' });
        window.addEventListener('pointermove', function (e) {
          const nx = e.clientX / window.innerWidth - 0.5;
          const ny = e.clientY / window.innerHeight - 0.5;
          xi(nx * -14); yi(ny * -8);
        }, { passive: true });
      }
    }
  }

  /* --------------------------------------------------------
     MARQUEE re-bind for any [data-marquee] added on new pages
  -------------------------------------------------------- */
  function initExtraMarquee() {
    if (!hasGSAP || reduce) return;
    document.querySelectorAll('[data-marquee]:not([data-marquee-bound])').forEach(function (track) {
      track.setAttribute('data-marquee-bound', '1');
      const tween = gsap.to(track, { xPercent: -50, duration: 24, ease: 'none', repeat: -1 });
      const sec = track.closest('.ticker') || track;
      sec.addEventListener('mouseenter', function () { gsap.to(tween, { timeScale: 0.25, duration: 0.4 }); });
      sec.addEventListener('mouseleave', function () { gsap.to(tween, { timeScale: 1, duration: 0.4 }); });
    });
  }

  /* --------------------------------------------------------
     TILT (data-tilt)
  -------------------------------------------------------- */
  function initTilt() {
    if (!fine || reduce || !hasGSAP) return;
    document.querySelectorAll('[data-tilt]').forEach(function (el) {
      el.addEventListener('mousemove', function (e) {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        gsap.to(el, { rotationY: px * 10, rotationX: -py * 10, duration: 0.4, ease: 'power2.out', transformPerspective: 800 });
      });
      el.addEventListener('mouseleave', function () { gsap.to(el, { rotationY: 0, rotationX: 0, duration: 0.6, ease: 'power2.out' }); });
    });
  }

  /* --------------------------------------------------------
     SPORT GRID — filter + reveal + spotlight (Find your community)
  -------------------------------------------------------- */
  function initSportGrid() {
    const grid = document.querySelector('[data-sport-grid]');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('.sport'));

    // cursor-follow spotlight
    if (fine) {
      cards.forEach(function (card) {
        card.addEventListener('mousemove', function (e) {
          const r = card.getBoundingClientRect();
          card.style.setProperty('--mx', (e.clientX - r.left) + 'px');
          card.style.setProperty('--my', (e.clientY - r.top) + 'px');
        });
      });
    }

    // staggered scroll reveal
    if (hasGSAP && typeof ScrollTrigger !== 'undefined' && !reduce) {
      gsap.set(cards, { opacity: 0, y: 34 });
      ScrollTrigger.batch(cards, {
        start: 'top 94%',
        onEnter: function (batch) {
          gsap.to(batch, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.05, overwrite: true });
        }
      });
    }

    // filters with staggered reflow
    const chips = Array.from(document.querySelectorAll('[data-sport-filter] .chip'));
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        chips.forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
        chip.setAttribute('aria-pressed', 'true');
        const f = chip.getAttribute('data-filter') || 'all';
        cards.forEach(function (card, i) {
          const match = f === 'all' || card.getAttribute('data-sport') === f;
          if (!hasGSAP || reduce) { card.hidden = !match; return; }
          if (match) {
            card.hidden = false;
            gsap.fromTo(card, { opacity: 0, y: 18, scale: 0.97 },
              { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'power3.out', delay: (i % 4) * 0.04, overwrite: true });
          } else {
            gsap.to(card, { opacity: 0, y: 10, scale: 0.97, duration: 0.28, overwrite: true,
              onComplete: function () { card.hidden = true; } });
          }
        });
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      });
    });
  }

  /* --------------------------------------------------------
     SCROLL PROGRESS RAIL (top, lime→blue, swells with speed)
  -------------------------------------------------------- */
  function initProgress() {
    if (reduce) return;
    const bar = document.createElement('div');
    bar.className = 'scroll-prog';
    bar.setAttribute('aria-hidden', 'true');
    const glow = document.createElement('div');
    glow.className = 'scroll-prog__glow';
    bar.appendChild(glow);
    document.body.appendChild(bar);

    let glowTimer = null;
    function update(y, velocity) {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(y / max, 1) : 0;
      bar.style.transform = 'scaleX(' + p + ')';
      if (velocity != null && Math.abs(velocity) > 12) {
        glow.style.opacity = '1';
        clearTimeout(glowTimer);
        glowTimer = setTimeout(function () { glow.style.opacity = '0'; }, 140);
      }
    }
    if (window.__lenis) {
      window.__lenis.on('scroll', function (e) { update(e.scroll, e.velocity); });
    } else {
      let last = 0;
      window.addEventListener('scroll', function () {
        const y = window.scrollY;
        update(y, y - last); last = y;
      }, { passive: true });
    }
    update(window.scrollY || 0, 0);
  }

  /* --------------------------------------------------------
     HERO AURA — the lime glow drifts toward the cursor
  -------------------------------------------------------- */
  function initHeroAura() {
    if (reduce || !fine) return;
    const hero = document.getElementById('hero');
    if (!hero) return;
    // Dedicated layer so it never fights the hero__glow scroll-parallax.
    const aura = document.createElement('div');
    aura.className = 'hero-aura';
    aura.setAttribute('aria-hidden', 'true');
    hero.appendChild(aura);
    let tx = 0, ty = 0, cx = 0, cy = 0, raf = null;
    hero.addEventListener('pointermove', function (e) {
      const r = hero.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width - 0.5) * 260;
      ty = ((e.clientY - r.top) / r.height - 0.5) * 200;
      if (!raf) raf = requestAnimationFrame(loop);
    });
    hero.addEventListener('pointerleave', function () { tx = 0; ty = 0; if (!raf) raf = requestAnimationFrame(loop); });
    function loop() {
      cx += (tx - cx) * 0.06; cy += (ty - cy) * 0.06;
      aura.style.transform = 'translate(-50%,-50%) translate(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px)';
      if (Math.abs(tx - cx) > 0.4 || Math.abs(ty - cy) > 0.4) raf = requestAnimationFrame(loop);
      else raf = null;
    }
  }

  /* --------------------------------------------------------
     IMAGE "LIME WIPE" reveal for product media tiles
  -------------------------------------------------------- */
  function initWipe() {
    if (reduce || typeof ScrollTrigger === 'undefined') return;
    document.querySelectorAll('.product__media').forEach(function (media) {
      const bar = document.createElement('span');
      bar.className = 'reveal-wipe';
      media.appendChild(bar);
      ScrollTrigger.create({
        trigger: media, start: 'top 88%', once: true,
        onEnter: function () { bar.classList.add('is-wiping'); }
      });
    });
  }

  /* ========================================================
     === ADDON: Sport Wheel — interactive sport selector ===
     A rotating-needle selector wheel for the Manifesto. Click a
     node, Spin for a random sport, or arrow-keys to step through.
     Hooked by [data-sportwheel] (initSportWheel). Guarded.
  -------------------------------------------------------- */
  function initSportWheel() {
    const root = document.querySelector('[data-sportwheel]');
    if (!root) return;
    const nodes = Array.prototype.slice.call(root.querySelectorAll('[data-wheel-node]'));
    if (!nodes.length) return;
    const wheel = root.querySelector('[data-wheel]');
    const needle = root.querySelector('[data-wheel-needle]');
    const nameEl = root.querySelector('[data-wheel-name]');
    const tagEl = root.querySelector('[data-wheel-tag]');
    const countEl = root.querySelector('[data-wheel-count]');
    const centerIcon = root.querySelector('[data-wheel-center-icon]');
    const ctaEl = root.querySelector('[data-wheel-cta]');
    const ctaLabel = root.querySelector('[data-wheel-cta-label]');
    const ctaPrefix = root.getAttribute('data-cta-prefix') || 'Explore';
    const step = 360 / nodes.length;
    let current = -1;
    let rot = 0;

    function pad(n) { return n < 10 ? '0' + n : '' + n; }

    function select(idx, spun) {
      idx = (idx % nodes.length + nodes.length) % nodes.length;
      const node = nodes[idx];
      const target = idx * step;
      if (spun) {
        const cur = ((rot % 360) + 360) % 360;
        rot += 720 + (((target - cur) % 360) + 360) % 360;
      } else {
        const cur = ((rot % 360) + 360) % 360;
        let delta = (((target - cur) + 180) % 360 + 360) % 360 - 180;
        rot += delta;
      }
      if (needle) needle.style.transform = 'translate(-50%, -100%) rotate(' + rot + 'deg)';
      nodes.forEach(function (n, k) {
        n.classList.toggle('is-active', k === idx);
        n.setAttribute('aria-selected', k === idx ? 'true' : 'false');
      });
      const name = node.getAttribute('data-name') || '';
      const tag = node.getAttribute('data-tag') || '';
      const url = node.getAttribute('data-url') || '';
      const emoji = node.getAttribute('data-emoji') || '';
      if (nameEl) nameEl.textContent = name;
      if (tagEl) tagEl.textContent = tag;
      if (countEl) countEl.textContent = pad(idx + 1) + ' / ' + pad(nodes.length);
      if (centerIcon) centerIcon.textContent = emoji;
      if (ctaLabel) ctaLabel.textContent = name ? (ctaPrefix + ' ' + name) : ctaPrefix;
      if (ctaEl) {
        // Prefer an explicit per-node URL; otherwise fall back to a Shopify product
        // search for the sport so the CTA is never a dead end (merchants often leave
        // data-url blank). Only truly empty when there's no name either.
        var href = url || (name ? '/search?q=' + encodeURIComponent(name) + '&type=product' : '');
        if (href) { ctaEl.setAttribute('href', href); ctaEl.classList.remove('is-disabled'); ctaEl.removeAttribute('aria-disabled'); }
        else { ctaEl.setAttribute('href', '#'); ctaEl.classList.add('is-disabled'); ctaEl.setAttribute('aria-disabled', 'true'); }
      }
      current = idx;
    }

    nodes.forEach(function (n, k) { n.addEventListener('click', function () { select(k, false); }); });

    root.querySelectorAll('[data-wheel-spin]').forEach(function (b) {
      b.addEventListener('click', function () {
        let next = current;
        if (nodes.length > 1) { while (next === current) next = Math.floor(Math.random() * nodes.length); }
        select(next, true);
      });
    });

    if (wheel) wheel.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); select(current + 1, false); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); select(current - 1, false); }
    });

    if (ctaEl) ctaEl.addEventListener('click', function (e) { if (ctaEl.classList.contains('is-disabled')) e.preventDefault(); });

    select(0, false);
  }

  /* ========================================================
     === ADDON: hero video carousel ===
     Plays the active slide's hosted video, pauses the rest,
     auto-advances, dots/arrows/swipe, pauses off-screen & on
     hover. Hooked by [data-hvc]. Remove this fn + boot() call
     + the hub-hero-video section/CSS to revert.
  -------------------------------------------------------- */
  function initHeroCarousel() {
    const root = document.querySelector('[data-hvc]');
    if (!root) return;
    const slides = Array.from(root.querySelectorAll('[data-hvc-slide]'));
    if (!slides.length) return;
    const dots = Array.from(root.querySelectorAll('[data-hvc-dot]'));
    const prev = root.querySelector('[data-hvc-prev]');
    const next = root.querySelector('[data-hvc-next]');
    const autoplay = root.dataset.autoplay === 'true' && !reduce;
    const interval = (parseFloat(root.dataset.interval) || 7) * 1000;
    let idx = 0, timer = null;

    function playVideo(i) {
      const v = slides[i] && slides[i].querySelector('[data-hvc-video]');
      if (v) { try { v.currentTime = 0; const p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {} }
    }
    function pauseVideo(i) {
      const v = slides[i] && slides[i].querySelector('[data-hvc-video]');
      if (v) { try { v.pause(); } catch (e) {} }
    }
    function arm() {
      clearTimeout(timer);
      if (!autoplay || slides.length < 2) return;
      const v = slides[idx].querySelector('[data-hvc-video]');
      const dur = (v && v.duration && isFinite(v.duration)) ? Math.max(interval, v.duration * 1000) : interval;
      timer = setTimeout(function () { go(idx + 1); }, dur);
    }
    function go(n) {
      const p = idx;
      idx = (n + slides.length) % slides.length;
      if (p !== idx) {
        slides[p].classList.remove('is-active'); pauseVideo(p);
        slides[idx].classList.add('is-active');
        if (dots[p]) dots[p].classList.remove('is-active');
        if (dots[idx]) dots[idx].classList.add('is-active');
        if (!reduce) playVideo(idx);
      }
      arm();
    }
    if (prev) prev.addEventListener('click', function () { go(idx - 1); });
    if (next) next.addEventListener('click', function () { go(idx + 1); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); }); });

    let dx0 = 0, down = false;
    root.addEventListener('pointerdown', function (e) { down = true; dx0 = e.clientX; });
    root.addEventListener('pointerup', function (e) {
      if (!down) return; down = false;
      const dd = e.clientX - dx0;
      if (Math.abs(dd) > 60) go(dd < 0 ? idx + 1 : idx - 1);
    });
    if (fine) {
      root.addEventListener('mouseenter', function () { clearTimeout(timer); });
      root.addEventListener('mouseleave', arm);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) {
        if (en[0].isIntersecting) { if (!reduce) playVideo(idx); arm(); }
        else { clearTimeout(timer); pauseVideo(idx); }
      }, { threshold: 0.2 }).observe(root);
    }
    if (!reduce) playVideo(0);
    arm();
  }

  /* ========================================================
     === ADDON: curated gallery ===
     Intro reveal + tap-to-open on touch (desktop expand is pure
     CSS hover). Hooked by [data-gallery-strip]. Remove this fn +
     boot() call + the hub-gallery section/CSS to revert.
  -------------------------------------------------------- */
  function initGallery() {
    const strip = document.querySelector('[data-gallery-strip]');
    if (!strip) return;
    const panels = Array.from(strip.querySelectorAll('[data-gpanel]'));
    if (!panels.length) return;
    if (hasGSAP && typeof ScrollTrigger !== 'undefined' && !reduce) {
      gsap.set(panels, { opacity: 0, y: 28 });
      ScrollTrigger.batch(panels, { start: 'top 92%', onEnter: function (b) {
        gsap.to(b, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.06, overwrite: true });
      }});
    }
    if (!fine) {
      panels.forEach(function (p) {
        p.addEventListener('click', function (e) {
          if (!p.classList.contains('is-open')) {
            const link = p.querySelector('.gpanel__link');
            if (link) e.preventDefault();
            panels.forEach(function (o) { o.classList.remove('is-open'); });
            p.classList.add('is-open');
          }
        });
      });
    }
  }
  /* === END ADDONS (orb / hero carousel / gallery) === */

  /* --------------------------------------------------------
     HERO ROTATING BACKGROUND MEDIA (videos / photos)
     === ADDON: crossfades the [data-hero-bg] slides behind the
     particle field; plays the active video, pauses the rest. ===
  -------------------------------------------------------- */
  function initHeroBg() {
    const wrap = document.querySelector('[data-hero-bg]');
    if (!wrap) return;
    const items = Array.prototype.slice.call(wrap.querySelectorAll('.hero-bgmedia__item'));
    if (!items.length) return;
    const smallScreen = window.matchMedia('(max-width: 900px)').matches;
    function loadImg(it) {  // lazy: assign background only when needed
      const el = it && it.querySelector('.hero-bgmedia__el[data-bg]');
      if (!el) return;
      // Phones get the 1200px variant; the 2200px original is desktop-only (2026-07-29).
      const src = (smallScreen && el.getAttribute('data-bg-m')) || el.getAttribute('data-bg');
      el.style.backgroundImage = 'url(' + src + ')';
      el.removeAttribute('data-bg'); el.removeAttribute('data-bg-m');
    }
    function playActive() {
      items.forEach(function (it) {
        const v = it.querySelector('video');
        if (!v) return;
        // a11y (WCAG 2.2.2): under prefers-reduced-motion, never autoplay the loop —
        // the <video poster> holds a static frame instead. No change for other users.
        if (it.classList.contains('is-active') && !reduce) { const p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else { try { v.pause(); } catch (e) {} }
      });
    }
    loadImg(items[0]);
    // Slide 2 used to be fetched during init, competing with the LCP image for bandwidth on the
    // very first paint. It is only needed at the first crossfade, so defer it until the browser
    // is idle (or ~2s in, for engines without requestIdleCallback).
    const warmNext = function () { loadImg(items[1 % items.length]); };
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(warmNext, { timeout: 3000 });
    else setTimeout(warmNext, 2000);
    // Phones get the smallest rendition. Desktop keeps the <source> Liquid chose.
    function prepVideo(it) {
      const v = it && it.querySelector('video[data-src-m]');
      if (!v) return;
      // The rendition is chosen now, so the browser may start fetching it in earnest (Liquid held the first slide at
      // preload="none" until this point so phones never pull the desktop file first).
      // Phone file in, and load() to RE-RUN source selection, all while preload is still "none" - only THEN allow
      // preload. The browser picked the 720p <source> at parse time, and raising preload resumes loading whatever
      // was already selected: setting src then preload opened (and aborted) a 720p request (measured 2026-10-13).
      const src = smallScreen && v.querySelector('source');
      if (src) { src.src = v.getAttribute('data-src-m'); try { v.load(); } catch (e) {} }
      if (v.hasAttribute('data-first')) v.setAttribute('preload', 'auto');
      v.removeAttribute('data-src-m');
    }
    // Lite (Save-Data / 2G / <4GB, set in theme.liquid): the poster IS the hero - never pull the ~1.8MB loop.
    // Measured 2026-10-13: the video was 43% of homepage bytes on a phone and nothing gated it.
    if (window.HUB_LITE) {
      items.forEach(function (it) { const v = it.querySelector('video'); if (v) { v.removeAttribute('data-src-m'); v.preload = 'none'; } });
      return;
    }
    // Start the loop after window load, not at DOMContentLoaded: the poster holds the frame, and the video no
    // longer competes with the page's own requests (load event -1.9s on a throttled phone, 2026-10-13).
    // advance() below calls playActive() itself, so the rotation is unchanged.
    const startVideo = function () { items.forEach(prepVideo); playActive(); };
    if (document.readyState === 'complete') startVideo(); else window.addEventListener('load', startVideo, { once: true });
    if (items.length < 2 || reduce) return;
    const interval = (parseFloat(wrap.getAttribute('data-interval')) || 6) * 1000;
    let idx = 0, timer = null;

    /* PER-SLIDE DWELL, not one setInterval for everything (2026-09-16).
       This used to be a single setInterval, which meant a video slide got
       exactly the same dwell as a photo — so the 13.3s hero teaser would be
       wiped away 3s in, and dropping the photo interval (which the owner
       asked for) made that worse, not better. A video slide now holds until
       it actually ENDS; photos keep the interval. The timer is a safety net,
       because a video that stalls or never fires `ended` would otherwise
       freeze the rotation permanently on slide one. */
    function advance() {
      // Outgoing slide holds fully visible underneath (is-prev) while the
      // incoming one wipes over it with the diagonal lime-edge clip (CSS).
      const prev = items[idx];
      prev.classList.remove('is-active');
      prev.classList.add('is-prev');
      setTimeout(function () { prev.classList.remove('is-prev'); }, 1500);
      idx = (idx + 1) % items.length;
      items[idx].classList.add('is-active');
      loadImg(items[idx]); loadImg(items[(idx + 1) % items.length]);
      playActive();
      schedule();
    }
    function schedule() {
      clearTimeout(timer);
      const v = items[idx].querySelector('video');
      if (v && !v.loop) {
        const done = function () { v.removeEventListener('ended', done); clearTimeout(timer); advance(); };
        v.addEventListener('ended', done);
        const len = (v.duration && isFinite(v.duration) && v.duration > 0) ? v.duration * 1000 : 15000;
        timer = setTimeout(done, len + 2500);
      } else {
        timer = setTimeout(advance, Math.max(2500, interval));
      }
    }
    schedule();
  }

  /* --------------------------------------------------------
     TRIBES SPOTLIGHT — hovering a tribe row floods the section
     background with that tribe's image (desktop hover only).
  -------------------------------------------------------- */
  function initTribeSpot() {
    if (!fine || reduce) return;
    const section = document.querySelector('.tribes');
    const spot = section && section.querySelector('[data-tribe-spot] .tribes__spot-img');
    if (!section || !spot) return;
    const rows = section.querySelectorAll('.tribe[data-spot]');
    if (!rows.length) return;
    let current = '';
    rows.forEach(function (row) {
      row.addEventListener('mouseenter', function () {
        const url = row.getAttribute('data-spot');
        if (url && url !== current) {
          current = url;
          spot.style.backgroundImage = 'url(' + url + ')';
        }
        section.classList.add('spot-on');
      });
      row.addEventListener('mouseleave', function () { section.classList.remove('spot-on'); });
    });
  }

  /* --------------------------------------------------------
     FOOTER — content parallax-uncovers as the page ends
  -------------------------------------------------------- */
  function initFooterReveal() {
    if (!hasGSAP || typeof ScrollTrigger === 'undefined' || reduce) return;
    const footer = document.querySelector('.footer');
    const inner = footer && footer.querySelector('.wrap');
    if (!footer || !inner) return;
    gsap.fromTo(inner, { y: -56, opacity: 0.75 }, {
      y: 0, opacity: 1, ease: 'none',
      scrollTrigger: { trigger: footer, start: 'top bottom', end: 'bottom bottom', scrub: true }
    });
  }

  /* --------------------------------------------------------
     MEDIA VIDEOS — play only while on-screen (perf at 50 slots)
     === ADDON: hub-media videos ship preload=none + no autoplay;
     this plays the visible ones and pauses the rest. ===
  -------------------------------------------------------- */
  function initMediaVideos() {
    const vids = document.querySelectorAll('video.hub-media--video');
    if (!vids.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      if (!reduce) vids.forEach(function (v) { const p = v.play(); if (p && p.catch) p.catch(function () {}); });
      return;
    }
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        const v = e.target;
        if (e.isIntersecting) { const p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else { try { v.pause(); } catch (err) {} }
      });
    }, { rootMargin: '250px 0px', threshold: 0.15 });
    vids.forEach(function (v) { io.observe(v); });
  }

  /* --------------------------------------------------------
     GEAR FINDER — interactive guided picker (data-finder)
  -------------------------------------------------------- */
  function initFinder() {
    const root = document.querySelector('[data-finder]');
    if (!root) return;
    const steps = Array.prototype.slice.call(root.querySelectorAll('[data-finder-step]'));
    if (!steps.length) return;
    const result = root.querySelector('[data-finder-result]');
    const dots = Array.prototype.slice.call(root.querySelectorAll('.finder__dot'));
    const resultLink = root.querySelector('[data-finder-result-link]');
    const defaultUrl = resultLink ? resultLink.getAttribute('href') : '';
    let cur = 0, lastUrl = '';
    function show(i) {
      steps.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
      if (result) result.classList.remove('is-active');
      dots.forEach(function (d, k) { d.classList.toggle('is-active', k <= i); });
      cur = i;
    }
    function showResult() {
      steps.forEach(function (s) { s.classList.remove('is-active'); });
      if (result) result.classList.add('is-active');
      dots.forEach(function (d) { d.classList.add('is-active'); });
      if (resultLink) resultLink.setAttribute('href', lastUrl || defaultUrl || '#');
    }
    root.querySelectorAll('[data-finder-opt]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const url = btn.getAttribute('data-finder-url');
        if (url) lastUrl = url;
        const stepEl = btn.closest('[data-finder-step]');
        const idx = parseInt(stepEl.getAttribute('data-finder-step'), 10);
        if (idx < steps.length - 1) show(idx + 1); else showResult();
      });
    });
    root.querySelectorAll('[data-finder-back]').forEach(function (b) {
      b.addEventListener('click', function () { if (cur > 0) show(cur - 1); });
    });
    const restart = root.querySelector('[data-finder-restart]');
    if (restart) restart.addEventListener('click', function () { lastUrl = ''; show(0); });
  }

  /* --------------------------------------------------------
     DROP COUNTDOWN (data-countdown)
  -------------------------------------------------------- */
  function initCountdown() {
    const root = document.querySelector('[data-countdown]');
    if (!root) return;
    const deadStr = (root.getAttribute('data-deadline') || '').trim();
    const timer = root.querySelector('[data-countdown-timer]');
    const cta = root.querySelector('[data-countdown-cta]');
    const live = root.querySelector('[data-countdown-live]');
    const out = { d: root.querySelector('[data-cd="d"]'), h: root.querySelector('[data-cd="h"]'), m: root.querySelector('[data-cd="m"]'), s: root.querySelector('[data-cd="s"]') };
    let target = NaN;
    if (deadStr) {
      const m = deadStr.match(/(\d{4})-(\d{1,2})-(\d{1,2})[ T]?(\d{1,2})?:?(\d{1,2})?/);
      if (m) target = new Date(+m[1], (+m[2]) - 1, +m[3], +(m[4] || 0), +(m[5] || 0), 0).getTime();
      if (isNaN(target)) { const d = Date.parse(deadStr.replace(' ', 'T')); if (!isNaN(d)) target = d; }
    }
    if (isNaN(target)) return;
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function setNum(el, val) {
      if (!el) return;
      const v = pad(val);
      if (el.textContent !== v) { el.textContent = v; if (!reduce) { el.classList.remove('is-tick'); void el.offsetWidth; el.classList.add('is-tick'); } }
    }
    let expired = false, ticker = null;
    function expire() { expired = true; if (ticker) { clearInterval(ticker); ticker = null; } if (timer) timer.style.display = 'none'; if (cta) cta.style.display = 'none'; if (live) live.hidden = false; }
    function tick() {
      const diff = target - Date.now();
      if (diff <= 0) { if (!expired) expire(); return; }
      const s = Math.floor(diff / 1000);
      setNum(out.d, Math.floor(s / 86400));
      setNum(out.h, Math.floor((s % 86400) / 3600));
      setNum(out.m, Math.floor((s % 3600) / 60));
      setNum(out.s, s % 60);
    }
    tick();
    if (!expired) ticker = setInterval(tick, 1000); // cleared in expire() so it stops ticking after the countdown ends
  }

  /* --------------------------------------------------------
     ANNOUNCEMENT BAR — rotate messages + dismiss (data-announce)
  -------------------------------------------------------- */
  function initAnnounce() {
    document.querySelectorAll('[data-announce]').forEach(function (bar) {
      const id = bar.getAttribute('data-announce-id');
      try { if (id && localStorage.getItem(id) === 'closed') { bar.hidden = true; return; } } catch (e) {}
      const close = bar.querySelector('[data-announce-close]');
      if (close) close.addEventListener('click', function () {
        bar.hidden = true; try { if (id) localStorage.setItem(id, 'closed'); } catch (e) {}
      });
      const track = bar.querySelector('[data-announce-track]');
      if (!track) return;
      const msgs = Array.prototype.slice.call(track.querySelectorAll('.hub-announce__msg'));
      if (msgs.length < 2 || reduce) return;
      const interval = (parseFloat(track.getAttribute('data-interval')) || 5) * 1000;
      let i = 0;
      setInterval(function () {
        msgs[i].classList.remove('is-active');
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('is-active');
      }, Math.max(3000, interval));
    });
  }

  /* --------------------------------------------------------
     SHOP THE LOOK — hotspots (data-lookbook)
  -------------------------------------------------------- */
  function initLookbook() {
    const stage = document.querySelector('[data-lookbook]');
    if (!stage) return;
    const spots = Array.prototype.slice.call(stage.querySelectorAll('[data-hotspot]'));
    spots.forEach(function (spot) {
      const btn = spot.querySelector('[data-hotspot-toggle]');
      if (btn) btn.addEventListener('click', function (e) {
        e.stopPropagation();
        const open = spot.classList.contains('is-open');
        spots.forEach(function (s) { s.classList.remove('is-open'); });
        if (!open) spot.classList.add('is-open');
      });
    });
    document.addEventListener('click', function () { spots.forEach(function (s) { s.classList.remove('is-open'); }); });
  }

  /* --------------------------------------------------------
     LOOKBOOK — multi-page flip-through gallery (data-lkb)
     Flip via arrows / swipe / dots / arrow-keys; hotspots open
     a mini card with add-to-cart (global data-quick-add). Guarded.
  -------------------------------------------------------- */
  function initLookbookGallery() {
    const root = document.querySelector('[data-lkb]');
    if (!root) return;
    const stage = root.querySelector('[data-lkb-stage]');
    const pages = Array.prototype.slice.call(root.querySelectorAll('[data-lkb-page]'));
    if (!pages.length) return;
    const prev = root.querySelector('[data-lkb-prev]');
    const next = root.querySelector('[data-lkb-next]');
    const dots = Array.prototype.slice.call(root.querySelectorAll('[data-lkb-dot]'));
    const curEl = root.querySelector('[data-lkb-current]');
    let i = 0;

    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function closeSpots() { root.querySelectorAll('.hotspot.is-open').forEach(function (s) { s.classList.remove('is-open'); }); }

    function go(n) {
      n = Math.max(0, Math.min(pages.length - 1, n));
      i = n;
      pages.forEach(function (p, k) {
        p.classList.toggle('is-active', k === i);
        p.classList.toggle('is-before', k < i);
        p.classList.toggle('is-after', k > i);
      });
      dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
      if (curEl) curEl.textContent = pad(i + 1);
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === pages.length - 1;
      closeSpots();
    }

    if (prev) prev.addEventListener('click', function () { go(i - 1); });
    if (next) next.addEventListener('click', function () { go(i + 1); });
    dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k); }); });
    if (stage) stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(i + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); }
    });

    // hotspot toggles (scoped to this lookbook)
    root.querySelectorAll('[data-hotspot]').forEach(function (spot) {
      const btn = spot.querySelector('[data-hotspot-toggle]');
      if (btn) btn.addEventListener('click', function (e) {
        e.stopPropagation();
        const open = spot.classList.contains('is-open');
        closeSpots();
        if (!open) spot.classList.add('is-open');
      });
    });
    document.addEventListener('click', function (e) { if (!e.target.closest('[data-hotspot]')) closeSpots(); });

    // swipe / drag to flip (ignores taps so hotspot clicks still work)
    let sx = 0, sy = 0, down = false;
    if (stage) {
      stage.addEventListener('pointerdown', function (e) { down = true; sx = e.clientX; sy = e.clientY; });
      stage.addEventListener('pointerup', function (e) {
        if (!down) return; down = false;
        const dx = e.clientX - sx, dy = e.clientY - sy;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) { go(dx < 0 ? i + 1 : i - 1); }
      });
      stage.addEventListener('pointercancel', function () { down = false; });
    }

    go(0);
  }

  /* --------------------------------------------------------
     BEFORE / AFTER compare slider (data-compare)
  -------------------------------------------------------- */
  function initCompare() {
    const stage = document.querySelector('[data-compare]');
    if (!stage) return;
    let dragging = false;
    function setPos(clientX) {
      const r = stage.getBoundingClientRect();
      let pct = ((clientX - r.left) / r.width) * 100;
      pct = Math.max(0, Math.min(100, pct));
      stage.style.setProperty('--pos', pct + '%');
    }
    stage.addEventListener('pointerdown', function (e) { dragging = true; setPos(e.clientX); if (stage.setPointerCapture) { try { stage.setPointerCapture(e.pointerId); } catch (x) {} } });
    stage.addEventListener('pointermove', function (e) { if (dragging) setPos(e.clientX); });
    window.addEventListener('pointerup', function () { dragging = false; });
    const handle = stage.querySelector('[data-compare-handle]');
    if (handle) handle.addEventListener('keydown', function (e) {
      const cur = parseFloat(getComputedStyle(stage).getPropertyValue('--pos')) || 50;
      if (e.key === 'ArrowLeft') { e.preventDefault(); stage.style.setProperty('--pos', Math.max(0, cur - 4) + '%'); }
      if (e.key === 'ArrowRight') { e.preventDefault(); stage.style.setProperty('--pos', Math.min(100, cur + 4) + '%'); }
    });
  }

  /* --------------------------------------------------------
     BACK-TO-TOP — floating button (data-to-top), Lenis-aware
  -------------------------------------------------------- */
  function initToTop() {
    const btn = document.querySelector('[data-to-top]');
    if (!btn) return;
    function upd() {
      const y = window.scrollY || document.documentElement.scrollTop;
      btn.classList.toggle('is-visible', y > 600);
    }
    upd();
    window.addEventListener('scroll', upd, { passive: true });
    btn.addEventListener('click', function () {
      if (window.__lenis && window.__lenis.scrollTo) window.__lenis.scrollTo(0);
      else window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  /* --------------------------------------------------------
     ARTICLE — reading-progress bar + copy-link (data-read-progress)
  -------------------------------------------------------- */
  function initArticle() {
    const wrap = document.querySelector('[data-read-progress]');
    if (wrap) {
      const bar = wrap.querySelector('i');
      if (bar) {
        var upd = function () {
          var h = document.documentElement;
          var max = h.scrollHeight - h.clientHeight;
          var p = max > 0 ? (window.scrollY || h.scrollTop) / max : 0;
          bar.style.transform = 'scaleX(' + Math.max(0, Math.min(1, p)) + ')';
        };
        upd();
        window.addEventListener('scroll', upd, { passive: true });
        window.addEventListener('resize', upd);
      }
    }
    document.querySelectorAll('[data-copy-link]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-copy-link');
        if (!url || !navigator.clipboard) return;
        navigator.clipboard.writeText(url).then(function () {
          var prev = btn.textContent;
          btn.textContent = 'Copied!';
          setTimeout(function () { btn.textContent = prev; }, 1600);
        }).catch(function () {});
      });
    });
  }

  /* --------------------------------------------------------
     KIT BUILDER — interactive product configurator (data-kit)
     Cycle each slot row, live total, toggle pieces, add the
     whole kit to the cart drawer in one request.
  -------------------------------------------------------- */
  function initKit() {
    const root = document.querySelector('[data-kit]');
    if (!root) return;
    const rows = Array.prototype.slice.call(root.querySelectorAll('[data-kit-row]'));
    const totalEl = root.querySelector('[data-kit-total]');
    const countEl = root.querySelector('[data-kit-count]');
    const addBtn = root.querySelector('[data-kit-add]');
    const fmt = (totalEl && totalEl.getAttribute('data-money-format')) || '${{amount}}';

    function formatMoney(cents) {
      const amount = cents / 100;
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
        if (key.indexOf('no_decimals') > -1) return Math.round(amount).toString();
        return amount.toFixed(2);
      });
    }

    rows.forEach(function (row) {
      const opts = Array.prototype.slice.call(row.querySelectorAll('[data-kit-opt]'));
      const dots = Array.prototype.slice.call(row.querySelectorAll('[data-kit-dot]'));
      let i = 0;
      opts.forEach(function (o, k) { if (o.classList.contains('is-active')) i = k; });
      function show(n) {
        if (!opts.length) return;
        i = (n % opts.length + opts.length) % opts.length;
        opts.forEach(function (o, k) { o.classList.toggle('is-active', k === i); });
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
        update();
      }
      const prev = row.querySelector('[data-kit-prev]');
      const next = row.querySelector('[data-kit-next]');
      if (prev) prev.addEventListener('click', function () { show(i - 1); });
      if (next) next.addEventListener('click', function () { show(i + 1); });
      dots.forEach(function (d, k) { d.addEventListener('click', function () { show(k); }); });
      const inc = row.querySelector('[data-kit-include]');
      if (inc) inc.addEventListener('change', function () { row.classList.toggle('is-off', !inc.checked); update(); });
      row._opts = opts; row._inc = inc; row._idx = function () { return i; };
    });

    function activeOf(row) { return row._opts[row._idx()]; }
    function included(row) { return !row._inc || row._inc.checked; }

    function update() {
      let cents = 0, count = 0, soldout = false;
      rows.forEach(function (row) {
        if (!included(row)) return;
        const o = activeOf(row); if (!o) return;
        count++;
        cents += parseInt(o.getAttribute('data-price'), 10) || 0;
        if (o.getAttribute('data-available') !== 'true') soldout = true;
      });
      if (totalEl) totalEl.textContent = count ? formatMoney(cents) : '—';
      if (countEl) countEl.textContent = count ? (count + (count === 1 ? ' piece' : ' pieces')) : 'Empty kit';
      if (addBtn) {
        addBtn.disabled = count === 0 || soldout;
        addBtn.classList.toggle('is-soldout', soldout);
      }
    }

    if (addBtn) addBtn.addEventListener('click', function () {
      const items = [];
      rows.forEach(function (row) {
        if (!included(row)) return;
        const o = activeOf(row); if (!o) return;
        const id = o.getAttribute('data-variant');
        if (id) items.push({ id: id, quantity: 1 });
      });
      if (!items.length) return;
      addBtn.disabled = true; addBtn.classList.add('is-loading');
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status && res.status >= 400) throw res;
          if (typeof window.__hubCartOpen === 'function') window.__hubCartOpen();
          else window.location.href = '/cart';
        })
        .catch(function () { window.location.href = '/cart'; })
        .then(function () { addBtn.disabled = false; addBtn.classList.remove('is-loading'); });
    });

    update();
  }

  /* --------------------------------------------------------
     PRODUCT PAGE — thumbs, image zoom, sticky ATC, recommendations
  -------------------------------------------------------- */
  function initProductExtras() {
    const hero = document.querySelector('.product-page__hero[data-zoom]');
    // thumbnails swap the hero image
    document.querySelectorAll('[data-thumb]').forEach(function (t) {
      t.addEventListener('click', function () {
        if (!hero) return;
        // srcset first: with a srcset present the browser ignores a new src, so swapping src alone would do nothing.
        const set = t.getAttribute('data-srcset'); if (set) hero.srcset = set; else hero.removeAttribute('srcset');
        const full = t.getAttribute('data-full'); if (full) hero.src = full;
        const z = t.getAttribute('data-zoom-src'); if (z) hero.setAttribute('data-zoom-src', z);
      });
    });
    // image zoom lightbox
    const zoom = document.querySelector('[data-zoom]');
    if (zoom) zoom.addEventListener('click', function () {
      const src = zoom.getAttribute('data-zoom-src') || zoom.src;
      const lb = document.createElement('div'); lb.className = 'zoom-lightbox';
      lb.innerHTML = '<img src="' + src + '" alt=""><button class="zoom-lightbox__close" type="button" aria-label="Close">&times;</button>';
      document.body.appendChild(lb);
      requestAnimationFrame(function () { lb.classList.add('is-open'); });
      function esc(e) { if (e.key === 'Escape') close(); }
      function close() { lb.classList.remove('is-open'); document.removeEventListener('keydown', esc); setTimeout(function () { lb.remove(); }, 300); }
      lb.addEventListener('click', close);
      document.addEventListener('keydown', esc); // removed inside close() regardless of how it's dismissed (was leaking on click-close)
    });
    // sticky add-to-cart
    const bar = document.querySelector('[data-sticky-atc]');
    const mainBtn = document.querySelector('[data-main-atc]');
    const form = document.querySelector('.product-page__form');
    if (bar && mainBtn && 'IntersectionObserver' in window) {
      // 2026-11-01: on phones the bar also shows while the real button is still BELOW the screen (on load the price and
      // Add to cart sat under the tab bar), so name + price + Add to cart are in the first view. It steps aside while the
      // real button is on screen. Desktop keeps the old rule (only after scrolling past). A hidden button never triggers it.
      var phoneMq = window.matchMedia ? window.matchMedia('(max-width: 760px)') : null;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var past = e.boundingClientRect.top < 0;
          var ahead = !!(phoneMq && phoneMq.matches) && mainBtn.offsetParent !== null && e.boundingClientRect.top >= window.innerHeight;
          bar.classList.toggle('is-visible', !e.isIntersecting && (past || ahead));
        });
      }, { threshold: 0 }).observe(mainBtn);
      const add = bar.querySelector('[data-sticky-add]');
      if (add && form) add.addEventListener('click', function () { if (form.requestSubmit) form.requestSubmit(); else form.submit(); });
    }
    // complementary recommendations
    const recs = document.querySelector('[data-recs]');
    if (recs && recs.getAttribute('data-url')) {
      fetch(recs.getAttribute('data-url')).then(function (r) { return r.text(); }).then(function (html) {
        const tmp = document.createElement('div'); tmp.innerHTML = html.trim();
        if (tmp.querySelector('.rec-card')) { recs.innerHTML = tmp.innerHTML; recs.hidden = false; }
      }).catch(function () {});
    }
  }

  /* === ADDON: mega-menu hover-intent =========================
     Adds a short close-delay so leaving the tab/panel diagonally (or dipping
     through a seam) doesn't instantly snap the mega shut. Pure-CSS :hover still
     opens it instantly; this only EXTENDS the open state, and keeps at most one
     panel open when swapping between adjacent tabs. Desktop pointer only
     (gated on `fine`). Pairs with the .is-mega-open CSS in the-hub.css.
     Revert: remove this fn, its boot() entry, and that CSS block. ----------- */
  function initMegaHoverIntent() {
    if (!fine) return;
    var items = Array.prototype.filter.call(
      document.querySelectorAll('.nav__item'),
      function (it) { return it.querySelector('.mega'); }
    );
    if (!items.length) return;
    var CLOSE_MS = 140;
    var closeTimer = null;
    var current = null;
    /* A hand-off between two tabs is NOT the same event as a close, and it must not
       look like one. All four panels are absolutely positioned at the same
       coordinates, so if the outgoing one fades out (0.35s) while the incoming one
       fades in, they cross-fade ON TOP OF EACH OTHER: the two sets of column
       headings superimpose ("THE LAB" over "THE PULSE" reads as "THE LABSE"), and
       because two half-opaque panels never sum to opaque, the hero photo bleeds
       straight through the middle of the menu. Marking the outgoing item makes it
       leave in the same frame the incoming one starts, so they never share pixels.
       CSS-only fallback: if this JS never runs the class is never added and the
       menu behaves exactly as it did before — the pure-CSS :hover open still works. */
    function clearSwapOut() {
      items.forEach(function (it) { it.classList.remove('is-mega-swapout'); });
    }
    function openItem(item) {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      /* clear first, so swapping A->B->A inside one gesture cannot leave the
         item you are returning to still marked as "on its way out" */
      clearSwapOut();
      if (current && current !== item) {
        current.classList.remove('is-mega-open');
        current.classList.add('is-mega-swapout');
      }
      current = item;
      item.classList.add('is-mega-open');
    }
    function scheduleClose() {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        /* a real close, not a hand-off — let it keep its designed 0.35s fade */
        clearSwapOut();
        if (current) current.classList.remove('is-mega-open');
        current = null; closeTimer = null;
      }, CLOSE_MS);
    }
    items.forEach(function (item) {
      item.addEventListener('mouseenter', function () { openItem(item); });
      item.addEventListener('mouseleave', scheduleClose);
    });
  }
  /* === END ADDON: mega-menu hover-intent === */

  function boot() {
    /* The hero's cursor-following aura (initHeroAura) is disabled (hero is
       reserved for video). Re-enable by adding initHeroAura to the list below.

       Each init runs in isolation: because sections can now live on separate
       pages, a missing element inside one init must never stop the rest of the
       site's JS. Any throw is caught + logged, then the next init still runs. */
    var inits = [
      initPageTrans, initHeroBg, initMediaVideos, initFinder, initCountdown,
      initAnnounce, initLookbook, initLookbookGallery, initCompare, initArticle,
      initToTop, initKit, initProductExtras, initSportWheel,
      initHeroCarousel, initGallery, initSportGrid, initScramble, initChars,
      initFeed, initWall, initBento, initHStrip, initStageTracker, initClip,
      initHeroParallax, initExtraMarquee, initTilt, initProgress, initWipe,
      initTribeSpot, initFooterReveal, initMegaHoverIntent
    ];
    for (var i = 0; i < inits.length; i++) {
      try { inits[i](); }
      catch (err) { if (window.console && console.warn) { console.warn('[hub] init skipped:', (inits[i] && inits[i].name) || i, err); } }
    }
    if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* === ADDON: store-policy page enhancement (2026-09-16) =======================
   The store policies (/policies/*) render through Shopify's built-in
   .shopify-policy__* markup, not a theme section (this theme rejects
   policy.json and does not route /policies/* through policy.liquid — verified
   2026-09-16). So there is no template to host an inline script and no section
   settings; this file, already loaded on every page, is the only hook.

   Pure progressive enhancement of that built-in markup: a breadcrumb, the
   "Legal" eyebrow, a "Last updated" chip and a jump-to-section list built from
   the <h2> headings. Styling lives in the matching ADDON fence in the-hub.css;
   with JS off the CSS alone still clears the header and reads cleanly. Runs
   only where .shopify-policy__container exists, wrapped in try/catch, so it
   cannot affect any other page. Remove this block + that CSS fence to retire.
   =========================================================================== */
(function () {
  function initHubPolicy() {
    try {
      var container = document.querySelector('.shopify-policy__container');
      if (!container || container.getAttribute('data-hub-policy')) return;
      var titleWrap = container.querySelector('.shopify-policy__title');
      var bodyWrap = container.querySelector('.shopify-policy__body');
      var rte = bodyWrap && (bodyWrap.querySelector('.rte') || bodyWrap);
      if (!titleWrap || !bodyWrap || !rte) return;
      container.setAttribute('data-hub-policy', '1');

      var h1 = titleWrap.querySelector('h1');
      var title = (h1 ? h1.textContent : document.title || 'Policy').trim();

      // breadcrumb + eyebrow, above the title
      var head = document.createElement('div');
      head.className = 'hub-policy__head';
      var crumb = document.createElement('div');
      crumb.className = 'hub-policy__crumb';
      var home = document.createElement('a');
      home.href = '/';
      home.textContent = 'Home';
      crumb.appendChild(home);
      crumb.appendChild(document.createTextNode(' / '));
      var cur = document.createElement('span');
      cur.className = 'lime';
      cur.textContent = title;
      crumb.appendChild(cur);
      var eye = document.createElement('span');
      eye.className = 'hub-policy__eyebrow';
      eye.textContent = 'Legal';
      head.appendChild(crumb);
      head.appendChild(eye);
      container.insertBefore(head, titleWrap);

      // "Last updated…" opener → chip, only when it really is one
      var firstP = rte.querySelector('p');
      if (firstP && /^\s*last updated/i.test(firstP.textContent || '')) {
        firstP.classList.add('hub-policy__updated');
      }

      // jump-to-section list from the <h2> headings
      var heads = rte.querySelectorAll('h2');
      if (heads.length < 2) return;
      var slug = function (t) {
        return (t || '').toLowerCase().trim()
          .replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-') || 'section';
      };
      var used = {};
      var toc = document.createElement('nav');
      toc.className = 'hub-policy__toc';
      toc.setAttribute('aria-label', 'On this page');
      var tocH = document.createElement('p');
      tocH.className = 'hub-policy__toc-h';
      tocH.textContent = 'On this page';
      toc.appendChild(tocH);
      var links = [];
      Array.prototype.forEach.call(heads, function (h) {
        var id = h.id || slug(h.textContent);
        if (used[id]) { id = id + '-' + used[id]++; } else { used[id] = 1; }
        h.id = id;
        var a = document.createElement('a');
        a.href = '#' + id;
        a.textContent = h.textContent;
        a.className = 'hub-policy__toc-link';
        toc.appendChild(a);
        links.push(a);
      });
      container.insertBefore(toc, bodyWrap);

      if ('IntersectionObserver' in window) {
        var byId = {};
        links.forEach(function (l) { byId[l.getAttribute('href').slice(1)] = l; });
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            links.forEach(function (l) { l.classList.remove('is-active'); });
            var l = byId[e.target.id];
            if (l) l.classList.add('is-active');
          });
        }, { rootMargin: '-18% 0px -72% 0px' });
        Array.prototype.forEach.call(heads, function (h) { io.observe(h); });
      }
    } catch (e) { /* enhancement only — never break the page */ }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHubPolicy);
  else initHubPolicy();
})();
